/**
 * Editor-internal serialization for converting between ProseMirror's HTML
 * representation and our markdown-style storage format ([text](url) links).
 *
 * NOT for rendering markdown to users — use react-markdown for that.
 * These functions exist solely to bridge ProseMirror ↔ API storage.
 */

// Shared link regex: matches [text](url) with support for one level of
// parentheses in URLs (e.g. Wikipedia: https://en.wikipedia.org/wiki/Test_(page)).
//
// Handles:
//   [text](https://example.com)
//   [wiki](https://en.wikipedia.org/wiki/Test_(page))
//   [x](https://a.com/b_(c)_d_(e))
//
// Does NOT handle:
//   [](url)           — [^\]]+ requires at least one char in link text
//   [x](url_(a_(b)))  — nested parens deeper than one level
//   [a \] b](url)     — escaped brackets in link text
const LINK_REGEX = /\[([^\]]+)\]\(([^()]*(?:\([^()]*\))*[^()]*)\)/g

/**
 * Parse a markdown-style string into HTML for ProseMirror's DOMParser.
 * Input: "Hello [Learn more](https://example.com) world"
 * Output: "<p>Hello <a href=\"https://example.com\">Learn more</a> world</p>"
 */
export function markdownToHtml(markdown: string): string {
  if (!markdown) return '<p></p>'

  // Process links first on raw markdown, then HTML-escape only text segments.
  // This avoids double-encoding ampersands in URLs (e.g. &amp; inside hrefs).
  const linkRegex = new RegExp(LINK_REGEX.source, LINK_REGEX.flags)
  const parts: string[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  while ((match = linkRegex.exec(markdown)) !== null) {
    // HTML-escape the text segment before this link
    if (match.index > lastIndex) {
      parts.push(escapeHtml(markdown.slice(lastIndex, match.index)))
    }
    const [, text, url] = match
    if (!/^https?:\/\//i.test(url)) {
      // Non-http link: render as escaped plain text
      parts.push(escapeHtml(text))
    } else {
      const safeUrl = url.replace(/&/g, '&amp;').replace(/"/g, '&quot;')
      const safeText = escapeHtml(text)
      parts.push(
        `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${safeText}</a>`
      )
    }
    lastIndex = match.index + match[0].length
  }
  // HTML-escape the remaining text after the last link
  if (lastIndex < markdown.length) {
    parts.push(escapeHtml(markdown.slice(lastIndex)))
  }

  return `<p>${parts.join('')}</p>`
}

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

/**
 * Serialize editor HTML back to a markdown-style string.
 * Extracts plain text and wraps linked text with [text](url).
 *
 * LIMITATION: Only handles <a> tags. If the ProseMirror schema is extended
 * to support bold/italic/other marks, this function must be updated to
 * serialize those marks as well.
 *
 * SAFETY: Uses innerHTML on a detached DOM element. Only call with
 * trusted/schema-produced HTML (e.g., ProseMirror output), not arbitrary user input.
 */
export function htmlToMarkdown(html: string): string {
  const div = document.createElement('div')
  div.innerHTML = html

  function walkNode(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent || ''
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement
      if (el.tagName === 'A') {
        const href = el.getAttribute('href') || ''
        const text = (el.textContent || '').replace(/\]/g, '\\]')
        const safeHref = href.replace(/\)/g, '%29')
        return `[${text}](${safeHref})`
      }
      const parts: string[] = []
      el.childNodes.forEach(child => {
        parts.push(walkNode(child))
      })
      return parts.join('')
    }
    return ''
  }

  const parts: string[] = []
  div.childNodes.forEach(child => {
    parts.push(walkNode(child))
  })
  return parts.join('').trim()
}
